// IDR unlock-card codes (Flow B — "unlock cards").
//
// A member buys an NS1/NS2 book at the IDR counter and gets a printed card
// carrying a QR and the human code. The QR is
// `https://hogc.one/scan/course/redeem/IDRU-EK1E-A0Y8-AWAD-K3NT`, so the code is
// the last path segment — never a query param (the cards are already cut and
// cannot be recalled, so the shape is fixed).
//
// What we know about the shape: an `IDRU` or `IDRV` prefix, then sixteen
// characters — twenty in all. `IDRC-` is a *collection pass* (Flow A, "collect
// the physical book at the counter"); IDR's unlock endpoints 404 it, so we tell
// it apart rather than burn a round trip and show "unknown code" for a card IDR
// knows perfectly well.
//
// What we deliberately do NOT do is validate. An earlier version enforced a
// twelve-character body, an alphabet with no I/L/O/U and a final letter — rules
// copied from the collection-pass format — and every real card was refused
// before IDR was asked. IDR minted the cards; IDR decides whether one is good.
// The app only tidies what was typed or scanned (case, dashes, whitespace) and
// sends it. A member who types the sixteen characters without the prefix is
// not corrected here either: the server supplies the prefix, because only it
// can try both.

const PREFIXES = ['IDRU', 'IDRV'];
const COLLECTION_PREFIX = 'IDRC';
// Prefix plus body, as printed. Only ever used to stop the typed field running
// on past a code — never to refuse one.
const CODE_LENGTH = 20;

// Everything that is not A–Z or 0–9 is noise a member may have typed or pasted:
// dashes, spaces, non-breaking spaces, the odd zero-width character from a PDF.
const NOISE = /[^A-Z0-9]/g;

/**
 * Strips a code down to its bare alphanumerics, upper-cased.
 * `"idru ek1e-a0y8 awad k3nt"` → `"IDRUEK1EA0Y8AWADK3NT"`.
 * @param {string} raw
 * @returns {string}
 */
export function stripCode(raw) {
  return String(raw ?? '')
    .toUpperCase()
    .replace(NOISE, '');
}

/**
 * Re-groups bare characters into dashed groups of four, without validating.
 * `"EK1EA0Y8"` → `"EK1E-A0Y8"`. Total: '' in, '' out.
 * @param {string} chars
 * @returns {string}
 */
function group(chars) {
  return (chars.match(/.{1,4}/g) || []).join('-');
}

/**
 * The unlock prefix a stripped code starts with, or '' when it carries none.
 * @param {string} stripped
 * @returns {string}
 */
function prefixOf(stripped) {
  return PREFIXES.find(prefix => stripped.startsWith(prefix)) || '';
}

/**
 * The canonical, dashed form of a code that carries an unlock prefix:
 * `IDRU-EK1E-A0Y8-AWAD-K3NT`. Returns '' for anything that does not start with
 * `IDRU` or `IDRV`, or has nothing after it — that is a routing answer, not a
 * judgement about the card: whatever follows the prefix goes through as-is for
 * IDR to decide.
 *
 * Deliberately total — it never throws — so a keystroke handler can call it on
 * every input event without a try/catch.
 * @param {string} raw
 * @returns {string}
 */
export function normalizeIdrCode(raw) {
  const stripped = stripCode(raw);
  const prefix = prefixOf(stripped);
  if (!prefix) return '';

  const body = stripped.slice(prefix.length);
  if (!body) return '';
  return `${prefix}-${group(body)}`;
}

/**
 * Whether `raw` carries an unlock code worth sending down the redeem lane. A
 * cheap routing check only — whether the card is real, unspent and unexpired
 * is IDR's call alone. Never gate the API call on more than this.
 * @param {string} raw
 * @returns {boolean}
 */
export function isIdrCode(raw) {
  return normalizeIdrCode(raw) !== '';
}

/**
 * True for an IDR *collection pass* (`IDRC-…`), which is Flow A: the member
 * collects a physical book at the counter. It is not an unlock and must never be
 * sent to the redeem endpoint. Lets the UI say something truer than "invalid".
 * @param {string} raw
 * @returns {boolean}
 */
export function isCollectionPass(raw) {
  return stripCode(raw).startsWith(COLLECTION_PREFIX);
}

/**
 * Formats the typed lane's input for display as the member types, without
 * validating. Shown as typed, grouped in fours, with a prefix kept as its own
 * group: `"ek1ea0y8"` → `"EK1E-A0Y8"`, `"idruek1e"` → `"IDRU-EK1E"`. Capped at
 * the printed length so the field cannot run on.
 * @param {string} raw
 * @returns {string}
 */
export function formatIdrCodeInput(raw) {
  const stripped = stripCode(raw).slice(0, CODE_LENGTH);
  const prefix = prefixOf(stripped);
  if (!prefix) return group(stripped);

  const body = stripped.slice(prefix.length);
  return body ? `${prefix}-${group(body)}` : prefix;
}

/**
 * What the typed lane sends: the code exactly as the member entered it, tidied.
 * With a prefix that is the canonical form; without one it is the bare
 * characters, grouped, and the server decides which prefix to try. Returns ''
 * when there is nothing to send — empty, a bare prefix, or a collection pass.
 * @param {string} raw
 * @returns {string}
 */
export function codeForSubmit(raw) {
  const stripped = stripCode(raw);
  if (!stripped || stripped.startsWith(COLLECTION_PREFIX)) return '';
  if (prefixOf(stripped)) return normalizeIdrCode(stripped);
  return group(stripped);
}
